<template>
  <div class="order__container">

    <div class="order__info">

      <div class="order-info">

        <button 
          v-if="!firstLoad && !order.is_ordered"
          @click="addingOrderDate = true"
          class="field button is-link is-fullwidth noprint">
          Mark as Ordered
        </button>

        <div v-if="!firstLoad && order.is_ordered" class="field">
          <label class="label">Ordered on</label> 
          <p v-if="order.ordered_on">{{ format(parse(order.ordered_on)) }}</p>
          <p v-else class="has-text-grey">No order date</p>
        </div>

        <div v-if="!firstLoad && order.is_ordered" class="field">
          <label class="label">Expected Delivery Date</label> 
          <p v-if="order.ex_delivery_date">{{ format(parse(order.ex_delivery_date)) }}</p>
          <p v-else class="has-text-grey">No expected delivery</p>
        </div>

        <button 
          v-if="!firstLoad && order.is_ordered && !order.is_delivered"
          @click="addingDeliveryDate = true"
          class="button is-link is-fullwidth noprint">
          Mark as Delivered
        </button>

        <div class="field" v-if="!loading && order.is_delivered">
          <label class="label">Delivered on</label> 
          <p>{{ format(parse(order.delivered_on)) }}</p>
        </div>

        <template v-if="!firstLoad">

          <div class="field">
            <label class="label">
              Order number
            </label>
            <p>{{ order.order_no }}</p>
          </div>

          <div class="field">
            <label class="label">
              Supplier
            </label>
            <v-select
              class="noprint"
              :disabled="!editable"
              v-if="suppliers.length > 0"
              required
              :options="suppliers"
              item-text="name"
              item-value="id"
              :value="order.supplier_id"
              @input="updateOrder({ 
                orderId: order.id, 
                data: { supplier_id: $event }
              })"
            />
            <span class="printonly">{{ order.supplier.name }}</span>
          </div>

          <div class="field">
            <label class="label">
              Description
            </label>
            <v-text-area
              class="noprint"
              :disabled="!editable"
              v-model="order.desc"
              rows="2"
              @blur="updateOrder({ 
                orderId: order.id, 
                data: { desc: $event }
              })">
            </v-text-area>
            <span class="printonly">{{ order.desc }}</span>
          </div>

          <div class="field">
            <label class="label">
              Currency
            </label>
            <v-select
              class="noprint"
              required
              :disabled="!editable"
              :options="currencyOptions"
              :value="order.currency"
              @input="updateOrder({ 
                orderId: order.id, 
                data: { currency: $event }
              })"/>
            <span class="printonly">{{ order.currency }}</span>
          </div>

          <div class="field">
            <label class="label">
              Price
            </label>
            <p v-if="orderPrice">
              {{ orderPrice | currency }}
            </p>
            <p v-else class="has-text-grey-light">
              $0
            </p>
          </div>
          
          <div class="field">
            <label class="label">
              Tax
            </label>
            <v-text-field
              class="noprint"
              :disabled="!editable"
              placeholder="Enter tax amount"
              v-model="order.tax"
              @blur="updateOrder({ orderId: order.id, data: {
                price: orderPrice,
                tax: $event,
                total_price: totalPrice
              }})"/>
            <span class="printonly">{{ order.tax | currency }}</span>
          </div>

          <div class="field">
            <label class="label">
              Shipping Cost
            </label>
            <v-text-field
              class="noprint"
              :disabled="!editable"
              placeholder="Enter shipping cost"
              v-model="order.shipping_delivery"
              @blur="updateOrder({ orderId: order.id, data: {
                price: orderPrice,
                shipping_delivery: $event,
                total_price: totalPrice
              }})"/>
            <span class="printonly">{{ order.shipping_delivery | currency }}</span>
          </div>

          <div class="field">
            <label class="label">
              Total Price
            </label>
            <p v-if="order.total_price">
              {{ order.total_price | currency }}
            </p>
            <p v-else class="has-text-grey-light">
              {{ (orderPrice + order.tax + order.shipping_delivery) | currency }}
            </p>
          </div>

        </template>
      </div>

    </div>

    <div class="order__details">

      <div class="buttons noprint" v-if="!firstLoad && editable">
        <button
          @click="addingBomDetail = true" 
          class="button is-primary">
          Add a BOM Item
        </button>
        <button
          @click="addingDetail = true" 
          class="button is-primary">
          Add an Unallocated Item
        </button>
        <button class="button"
          v-if="selected.length > 0"
          @click="launchConfirmDelete()">
          <span class="icon is-medium">
            <i class="fas fa-trash has-text-danger"></i>
          </span>
          <span>Delete {{ selected.length }} Selected Details</span>
        </button>
      </div>

      <article 
        v-if="!firstLoad && order.details.length == 0" 
        class="message is-info is-fullwidth">
        <div class="message-body">
          There are no details for this order.
        </div>
      </article>

      <table 
        :key="$route.params.orderId"
        v-if="!firstLoad && order.details.length > 0" 
        class="table is-hoverable is-clickable is-fullwidth has-small-font">
        <thead>
          <tr>
            <th class="noprint" v-if="editable">
              <v-checkbox
                :value="allSelected"
                @input="toggleAllSelected($event)">
              </v-checkbox>
            </th>
            <th 
              v-for="(header, headerIndex) in headers" 
              :key="headerIndex">
              {{ header.label }}
            </th>
            <th v-if="order.is_delivered">
              Warehouse Location
            </th>
            <th class="noprint" v-if="!order.is_ordered || order.is_delivered">
            </th>
          </tr>
        </thead>

        <tbody>

          <template v-for="detail in order.details">
            <tr 
              @click="toggleOpen(detail.id)"
              class="v-centered subheader"
              :key="detail.id">
              <td class="noprint" v-if="editable">
                <v-checkbox
                  @click.stop=""
                  :value="selected.indexOf(detail.id) != -1"
                  @input="toggleSelected(detail.id)">
                </v-checkbox>
              </td>
              <td>
                {{ detail.product_master ? 
                  detail.product_master.product_category.category : '' }}
              </td>
              <td>
                {{ detail.product_master ? detail.product_master.name : '' }}
              </td>
              <td clas="text--nowrap">
                <span v-if="detail.poduct_master && 
                  detail.product_master.product_category.has_dimensions">
                  L{{ detail.stocking_unit.length_in_str }}" x
                  W{{ detail.stocking_unit.width_in_str }}" x
                  T{{ detail.stocking_unit.thick_in_str }}"
                </span>
              </td>
              <td>
                <span v-if="editingId != detail.id">
                  {{ detail.qty_unassigned }}
                </span>
                <v-text-field 
                  v-else
                  v-model="detail.qty_unassigned"/>
              </td>
              <td>
                {{ detail.qty_assigned | commasep(0) }}
              </td>
              <td>
                {{ detail.total_qty | commasep(0) }}
              </td>
              <td>
                <span v-if="detail.id != editingId">
                  {{ detail.total_price | currency(2) }}
                </span>
                <v-text-field
                  @click.stop="" 
                  v-else
                  v-model="detail.total_price">
                </v-text-field>
              </td>

              <td>
                <span 
                  v-if="!!detail.total_price && !!detail.total_qty && detail.total_qty != 0">
                  {{ detail.total_price / detail.total_qty | currency }}
                </span>
              </td>

              <!-- Warehouse location, if order is delivered -->
              <td class="is-size-7" v-if="order.is_delivered">
                <template v-if="detail.order_detail_storage">
                  <div 
                    v-for="storage in detail.order_detail_storage"
                    :key="storage.id">
                    <span v-if="storage.storage_unit">
                      {{ storage.storage_unit.qty }} in 
                      {{ storage.storage_unit.storage ? 
                        storage.storage_unit.storage.storage_type.storage_type_name : '' }} @ 
                      {{ storage.storage_unit.storage ? 
                        storage.storage_unit.storage.warehouse_location.location_code : '' }}
                    </span>
                  </div>
                </template>
                <span class="has-text-grey-light" v-else>
                  No warehouse location
                </span>
              </td>

              <td 
                class="noprint" 
                v-if="!order.is_ordered || order.is_delivered">
                <div 
                  class="buttons buttons-right"
                  v-if="!order.is_ordered">
                  <template v-if="detail.id != editingId">
                    <button
                      @click.stop="editingId = detail.id"
                      class="button is-small">
                      <span class="has-text-grey icon">
                        <i class="fas fa-edit"></i>
                      </span>
                    </button>
                    <button  
                      @click.stop="launchConfirmDelete(detail.id)"
                      class="button is-small">
                      <span class="has-text-grey icon">
                        <i class="fas fa-trash"></i>
                      </span>
                    </button>
                  </template>
                  <template v-else>
                    <button  
                      @click.stop="saveEdits(detail)"
                      class="button is-small is-primary">
                      <span class="icon">
                        <i class="fas fa-check"></i>
                      </span>
                    </button>
                    <button  
                      @click.stop="editingId = null"
                      class="button is-small">
                      <span class="icon has-text-grey">
                        <i class="fas fa-times"></i>
                      </span>
                    </button>
                  </template>
                </div>

                <div
                  class="buttons buttons-right noprint"
                  v-if="order.is_delivered">

                  <button
                    v-if="!detail.is_fully_stored"
                    @click.stop="
                      storageAssignment.orderDetail = detail;
                      storageAssignment.state = true"
                    class="button is-small is-primary">
                    <span class="icon">
                      <i class="fas fa-arrow-right"></i>
                    </span>
                    <span>Add Storage Details</span>
                  </button>

                </div>
              </td>
            </tr>

            <!-- Bom Details -->
            <template v-if="openRows.includes(detail.id)">
              <template v-if="detail.order_boms.length > 0">
                <tr
                  class="v-centered is-narrow has-background-light has-tiny-font" 
                  :key="`${detail.id}-boms-header`">
                  <th v-if="editable"></th>
                  <th colspan="2">Project Name</th>
                  <th>BOM Name</th>
                  <th>Qty Needed</th>
                  <th>Qty To be Ordered</th>
                  <th colspan="3"></th>
                  <th v-if="order.is_delivered"></th>
                  <th class="noprint" v-if="!order.is_ordered || order.is_delivered">
                  </th>
                </tr>
                <tr 
                  class="v-centered is-narrow has-background-light"
                  v-for="bom in detail.order_boms"
                  :key="`bom-${bom.id}`">
                  <td class="noprint" v-if="editable"></td>
                  <td colspan="2">
                    {{ bom.bom_detail.bom.project.project_name }}
                  </td>
                  <td>
                    <router-link
                      :to="{ 
                        name: 'BomViewer',
                        params: { bomId: bom.bom_detail.bom.id }
                      }">
                      {{ bom.bom_detail.bom.name }}
                    </router-link>
                  </td>
                  <td>
                    {{ bom.bom_detail.finished_qty | commasep(0) }}
                  </td>
                  <td>
                    <span v-if="bomDetailEditingId != bom.id">
                      {{ bom.qty_to_be_ordered | commasep(0) }}
                    </span>
                    <v-text-field 
                      v-else
                      v-model="bom.qty_to_be_ordered"/>
                  </td>
                  <td colspan="3"></td>
                  <td v-if="order.is_delivered"></td>
                  <td class="noprint" v-if="!order.is_ordered || order.is_delivered">
                    <div 
                      class="buttons buttons-right" 
                      v-if="editable">
                      <template v-if="bomDetailEditingId != bom.id">
                        <button
                          @click="bomDetailEditingId = bom.id"
                          class="button is-small">
                          <span class="has-text-grey icon">
                            <i class="fas fa-edit"></i>
                          </span>
                        </button>
                        <button  
                          @click="launchConfirmDeleteBomDetail(detail.id, bom.id)"
                          class="button is-small">
                          <span class="icon has-text-grey">
                            <i class="fas fa-trash"></i>
                          </span>
                        </button>
                      </template>
                      <template v-else>
                        <button  
                          @click.stop="saveBomDetailEdits(bom)"
                          class="button is-small is-primary">
                          <span class="icon">
                            <i class="fas fa-check"></i>
                          </span>
                        </button>
                        <button  
                          @click.stop="bomDetailEditingId = null"
                          class="button is-small">
                          <span class="icon has-text-grey">
                            <i class="fas fa-times"></i>
                          </span>
                        </button>
                      </template>
                    </div>
                  </td>
                </tr>
              </template>
            </template>
            
          </template>

        </tbody>
      </table>
    </div>

    <v-confirm-modal 
      v-model="confirmDelete.state"
      :title="confirmDelete.title"
      :message="confirmDelete.message"
      :type="confirmDelete.type"
      @confirm="deleteConfirmedCb()"
      @cancel="confirmDelete.state = false"/>

    <bom-details-selector-modal
      v-if="!firstLoad && editable"
      v-model="addingBomDetail"
      :order-id="order.id"/>

    <add-order-detail-modal
      v-if="!firstLoad && editable"
      v-model="addingDetail"
      :order-id="order.id"/>

    <store-order-item-modal
      v-model="storageAssignment.state"
      :order-detail="storageAssignment.orderDetail"/>

    <add-order-date-modal 
      v-model="addingOrderDate"
      @set-dates="updateOrder({ 
        orderId: order.id, 
        data: $event
      })"/>

    <v-datepicker-modal 
      v-model="addingDeliveryDate"
      title="Add Delivery Date"
      @change="updateOrder({ 
        orderId: order.id, 
        data: { delivered_on: $event }
      })"/>

  </div>
</template>

<script>
import VSelect from '@/components/base/VSelect'
import VConfirmModal from '@/components/base/VConfirmModal'
import VTextField from '@/components/base/VTextField'
import VTextArea from '@/components/base/VTextArea'
import VCheckbox from '@/components/base/VCheckbox'
import VDatepickerModal from '@/components/base/VDatepickerModal'

import AddOrderDetailModal from '@/components/inventory/AddOrderDetailModal'
import BomDetailsSelectorModal from '@/components/inventory/BomDetailsSelectorModal'
import AddOrderDateModal from '@/components/inventory/AddOrderDateModal'
import StoreOrderItemModal from '@/components/inventory/StoreOrderItemModal'

import { mapState, mapActions, mapMutations } from 'vuex'

import { parseISO, format } from 'date-fns'

export default {
  components: { 
    VConfirmModal,
    VTextField,
    VCheckbox,
    VSelect,
    VTextArea,
    VDatepickerModal,
    AddOrderDetailModal,
    BomDetailsSelectorModal,
    AddOrderDateModal,
    StoreOrderItemModal
  },
  data() {
    return {
      headers: [
        { label: 'Product Category' },
        { label: 'Product Name' },
        { label: 'Product Size' },
        { label: 'Unassigned Quantity' },
        { label: 'Assigned Quantity' },
        { label: 'Total Quantity' },
        { label: 'Price' },
        { label: 'Price per Unit'}
      ],
      confirmDelete: {
        state: false,
        title: 'Delete Confirmation',
        message: 'Are you sure you want to delete this BOM item from this Order?',
        type: 'danger'
      },
      deleteConfirmedCb: null,
      editingId: null,
      addingDetail: false,
      bomDetailEditingId: null,
      addingBomDetail: false,
      open: false,
      openRows: [],
      parse: (d) => {
        return d ? parseISO(d) : null
      },
      format: (d) => {
        return d ? format(d, 'yyyy-MM-dd H:mm:ss') : null
      },
      addingOrderDate: false,
      addingDeliveryDate: false,
      storageAssignment: {
        state: false,
        orderDetail: null
      }
    }
  },
  computed: {
    ...mapState("suppliers", ["suppliers"]),
    ...mapState("order", [
      "order", "selected", "allSelected", "loading", "firstLoad"
    ]),
    ...mapState("orders", ["currencyOptions"]),
    editable() {
      return !this.order.is_delivered
    },
    orderPrice() {
      return this.order.details.map(d => d.total_price)
        .reduce((a, b) => parseFloat(a) + parseFloat(b), 0);
    },
    totalPrice() {
      return parseFloat(this.orderPrice || 0) + parseFloat(this.order.tax || 0) + parseFloat(this.order.shipping_delivery || 0)
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("order", [
      "getOrder", "updateOrder", "updateDetail", "deleteDetail", 
      "updateBomDetail", "deleteBomDetail", "toggleAllSelected"
    ]),
    ...mapActions("suppliers", ["getSuppliers"]),
    ...mapMutations("order", [
      "setOrder", "deselectAll", "toggleSelected", "setFirstLoad", "setLoading"
    ]),
    initialize() {
      if (this.$route.params.order) {
        this.setOrder(this.$route.params.order);
        this.setFirstLoad(false);
        this.setLoading(false);
      } else {
        this.getOrder(this.$route.params.orderId);
      }

      if (this.suppliers.length == 0) {
        this.getSuppliers({ per_page: 100 });
      }
    },
    saveEdits(detail) {
      this.updateDetail({ 
        detailId: detail.id, 
        data: { 
          qty_unassigned: detail.qty_unassigned,
          total_price: detail.total_price
        }
      }).then(
        () => {
          this.editingId = null;
          this.updateOrder({
            orderId: this.order.id,
            data: {
              total_price: this.totalPrice
            }
          });
        }
      );
    },
    saveBomDetailEdits(bomDetail) {
      this.updateBomDetail({ 
        bomDetailId: bomDetail.id, 
        data: { 
          qty_to_be_ordered: bomDetail.qty_to_be_ordered
        }
      }).then(
        () => {
          this.bomDetailEditingId = null
        }
      );
    },
    launchConfirmDelete(detailId) {
      if (!detailId) {
        this.deleteConfirmedCb = () => {
          this.deleteSelected();
        };
        this.confirmDelete.message = `Are you sure you want to delete the ${this.selected.length} selected order details?`;
      } else {
        this.deleteConfirmedCb = () => {
          this.deleteConfirmed(detailId);
        };
        this.confirmDelete.message = 'Are you sure you want to delete this order detail?'
      }

      this.confirmDelete.state = true;
    },
    launchConfirmDeleteBomDetail(orderDetailId, bomDetailId) {
      this.deleteConfirmedCb = () => {
        this.deleteBomConfirmed(orderDetailId, bomDetailId);
      };

      this.confirmDelete.message = 'Are you sure you want to remove this bom detail from this order?'
      this.confirmDelete.state = true;
    },
    deleteConfirmed(id) {
      this.confirmDelete.state = false;

      if (!id) {
        this.deleteSelected();
      } else {
        this.deleteDetail(id).then(
          () => {
            this.confirmDelete.state = false;
            if (this.selectedIndexOf(id) != -1) {
              this.toggleSelected(id);
            }
          }
        );
      }
    },
    deleteBomConfirmed(orderDetailId, bomDetailId) {
      this.deleteBomDetail({ orderDetailId, bomDetailId });
      this.confirmDelete.state = false;
    },
    deleteSelected() {
      this.selected.forEach(id => {
        this.deleteDetail(id);
      })
      this.confirmDelete.state = false;
    },
    toggleOpen(id) {
      let index = this.openRows.indexOf(id);
      if (index == -1) {
        this.openRows.push(id);
      } else {
        this.openRows.splice(index, 1);
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/printable.scss';
@import '@/styles/table.scss';

.order {
  &__container {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
  }
  &__info {
    padding: 1.5em;
    width: 20%;
  }
  &__details {
    padding-right: 1.5em;
    padding-top: 1.5em;
    padding-bottom: 1.5em;
    width: 80%;
  }
}

@media print {
  .order {
    &__info {
      width: 100%;
      padding: 0.5em 0em;
    }
    &__details {
      width: 100%;
      padding: 0em;
    }
  }
  .table {
    font-size: 0.75em !important;
  }
  .order-info {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    font-size: 0.8em;
    margin: 0em -0.8em;
    .label {
      font-size: 0.85em;
    }
    .field {
      padding: 0em 1em;
    }
  }
}
</style>